import { onBeforeUnmount } from 'vue';

import debounce from 'lodash/debounce';

import { useDefaultToast } from '@/composables/useDefaultToast';
import workerService from '@/modules/worker/services/workerService';

import type { WorkerDetail } from '@/modules/worker/types';

const DEBOUNCE_MS = 800;

export interface DebouncedPatchWorkerKeywordsGetters {
	workerId: () => string;
	worker: () => WorkerDetail;
	refreshWorker?: () => Promise<void>;
}

export const useDebouncedPatchWorkerKeywords = (
	getters: DebouncedPatchWorkerKeywordsGetters
) => {
	const { toastError } = useDefaultToast();

	let debouncedPatchKeywords: ReturnType<typeof debounce> | null = null;

	const rebuildDebouncedPatchKeywords = () => {
		debouncedPatchKeywords?.cancel();
		debouncedPatchKeywords = debounce((words: string[]) => {
			void (async () => {
				try {
					await workerService.patchWorkerKeywords(getters.workerId(), {
						keywords: words,
					});
					getters.worker().keywords = words;
					await getters.refreshWorker?.();
				} catch {
					toastError();
					await getters.refreshWorker?.();
				}
			})();
		}, DEBOUNCE_MS);
	};

	rebuildDebouncedPatchKeywords();

	onBeforeUnmount(() => {
		debouncedPatchKeywords?.cancel();
	});

	const schedulePatchWorkerKeywords = (words: string[]) => {
		debouncedPatchKeywords?.(words);
	};

	return { schedulePatchWorkerKeywords };
};
